// src/pages/Jobs/JobsIndex.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Save, Trash2, SlidersHorizontal } from "lucide-react";

import AppShell from "@/components/AppShell";
import DataTable, { Col } from "@/components/DataTable";
import EmptyState from "@/components/EmptyState";
import Skeleton from "@/components/Skeleton";
import { useNotifications } from "@/lib/notifications";
import { get, set } from "@/lib/storage";
import { logAudit } from "@/lib/audit";

/** ============================================================================
 * Types & Constants
 * ========================================================================== */
type Role = "Admin" | "Dispatcher" | "Loader" | "Trucker" | "Accountant";
type JobStatus = "Draft" | "Active" | "Paused" | "Completed" | "Archived";

type Job = {
  id: string;
  code?: string;
  name: string;
  site?: string;
  customer?: string;
  status: JobStatus;
  startDate?: string;
  endDate?: string;
  loads?: number;
  tons?: number;
  estimatedTonnage?: number;
  profit?: number;
  tags?: string[];
  hasGeofence?: boolean;
  createdAt?: string;
};

type Filters = {
  q: string;
  status: "any" | JobStatus;
  hasGeofence: "any" | "yes" | "no";
  start?: string;
  end?: string;
  density: "cozy" | "compact";
};

type Preset = {
  id: string;
  name: string;
  filters: Omit<Filters, "density">;
};

const SK = {
  table: "tbl:jobs:v1",
  data: "jobs:data:v1",
  presets: "jobs:presets:v1",
  uiDensity: "ui:table:density",
} as const;

const DEFAULT_FILTERS: Filters = {
  q: "",
  status: "any",
  hasGeofence: "any",
  start: "",
  end: "",
  density: get<Filters["density"]>(SK.uiDensity, "cozy"),
};

/** ============================================================================
 * Utils
 * ========================================================================== */
const fmtCurrency = (n: number | undefined | null) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      })
    : "";

const fmtInt = (n: number | undefined | null) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : "";

const withinRange = (d?: string, start?: string, end?: string) => {
  if (!d) return true;
  const t = new Date(d).getTime();
  if (start) {
    const s = new Date(start).getTime();
    if (t < s) return false;
  }
  if (end) {
    const e = new Date(end).getTime();
    if (t > e) return false;
  }
  return true;
};

const matches = (job: Job, q: string) => {
  if (!q) return true;
  const hay = [
    job.name,
    job.code,
    job.site,
    job.customer,
    job.status,
    job.tags?.join(" "),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(q.toLowerCase());
};

const canCreate = (role: Role) => role === "Admin" || role === "Dispatcher";
const canEdit = (role: Role) => role === "Admin" || role === "Dispatcher";

// Optional sample data for empty-state seeding
const SAMPLE: Job[] = [
  {
    id: "J-1001",
    code: "JB-1001",
    name: "Rhodes East Thin",
    site: "Rhodes East",
    customer: "W. McLeod Rhodes Co.",
    status: "Active",
    startDate: "2025-08-12",
    loads: 48,
    tons: 1220,
    estimatedTonnage: 3000,
    profit: 28450,
    tags: ["thin", "pine"],
    hasGeofence: true,
    createdAt: "2025-08-01T09:18:00Z",
  },
  {
    id: "J-1002",
    code: "JB-1002",
    name: "Essex Final Harvest",
    site: "Essex West Block A",
    customer: "Essex Farms LLC",
    status: "Paused",
    startDate: "2025-09-05",
    loads: 12,
    tons: 330,
    estimatedTonnage: 5200,
    profit: 18700,
    tags: ["final", "export"],
    hasGeofence: false,
    createdAt: "2025-09-03T14:22:00Z",
  },
  {
    id: "J-1003",
    code: "JB-1003",
    name: "Branchville CNS Push",
    site: "Branchville North",
    customer: "Canopy Timber",
    status: "Draft",
    loads: 0,
    tons: 0,
    estimatedTonnage: 1600,
    profit: 0,
    tags: ["cns"],
    hasGeofence: false,
    createdAt: "2025-09-15T12:05:00Z",
  },
];

/** ============================================================================
 * Component
 * ========================================================================== */
export default function JobsIndex() {
  const navigate = useNavigate();
  const { push } = useNotifications(); // <- library exposes push()

  const role = get<Role>("role", "Admin");

  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);

  const [presets, setPresets] = useState<Preset[]>(
    () => get<Preset[]>(SK.presets, [])
  );
  const [activePresetId, setActivePresetId] = useState<string>("");

  useEffect(() => {
    setLoading(true);
    const rows = get<Job[]>(SK.data, []);
    setJobs(rows);
    setLoading(false);
  }, []);

  useEffect(() => {
    set(SK.uiDensity, filters.density);
  }, [filters.density]);

  const rows = useMemo(() => {
    return jobs.filter((j) => {
      if (!matches(j, filters.q)) return false;

      if (filters.status !== "any" && j.status !== filters.status) return false;

      if (filters.hasGeofence !== "any") {
        const wanted = filters.hasGeofence === "yes";
        if (!!j.hasGeofence !== wanted) return false;
      }

      const refDate = j.startDate || j.createdAt;
      if (!withinRange(refDate, filters.start || undefined, filters.end || undefined))
        return false;

      return true;
    });
  }, [jobs, filters]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.loads += r.loads || 0;
        acc.tons += r.tons || 0;
        acc.est += r.estimatedTonnage || 0;
        acc.profit += r.profit || 0;
        return acc;
      },
      { loads: 0, tons: 0, est: 0, profit: 0 }
    );
  }, [rows]);

  // Columns (DataTable.Col is NOT generic in this codebase)
  const cols: Col[] = useMemo(
    () => [
      {
        key: "name",
        header: "Job",
        width: 280,
        render: (r: Job) => (
          <div className="pl-2">
            <div className="font-medium">{r.name}</div>
            <div className="text-xs opacity-70">
              {r.code ? `${r.code}` : "—"} • {r.customer || "—"}
            </div>
          </div>
        ),
      },
      {
        key: "site",
        header: "Site",
        width: 220,
        render: (r: Job) => (
          <div>
            <div>{r.site || "—"}</div>
            {r.hasGeofence ? <span className="badge ml-0 mt-1">Geofence</span> : null}
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        width: 120,
        render: (r: Job) => (
          <span
            className={`badge ${
              r.status === "Active"
                ? ""
                : r.status === "Draft"
                ? "opacity-70"
                : r.status === "Paused"
                ? "bg-gray-200"
                : r.status === "Completed"
                ? "bg-green-100"
                : "bg-gray-100"
            }`}
          >
            {r.status}
          </span>
        ),
      },
      {
        key: "startDate",
        header: "Start",
        width: 120,
        render: (r: Job) => (r.startDate ? new Date(r.startDate).toLocaleDateString() : "—"),
      },
      {
        key: "endDate",
        header: "End",
        width: 120,
        render: (r: Job) => (r.endDate ? new Date(r.endDate).toLocaleDateString() : "—"),
      },
      {
        key: "loads",
        header: "Loads",
        align: "right",
        width: 90,
        className: "tabular-nums text-right",
        render: (r: Job) => fmtInt(r.loads ?? 0),
      },
      {
        key: "tons",
        header: "Tons",
        align: "right",
        width: 110,
        className: "tabular-nums text-right",
        render: (r: Job) => fmtInt(r.tons ?? 0),
      },
      {
        key: "estimatedTonnage",
        header: "Est. Tons",
        align: "right",
        width: 120,
        className: "tabular-nums text-right",
        render: (r: Job) => fmtInt(r.estimatedTonnage ?? 0),
      },
      {
        key: "profit",
        header: "Profit",
        align: "right",
        width: 130,
        className: "tabular-nums text-right",
        render: (r: Job) => fmtCurrency(r.profit ?? 0),
      },
      {
        key: "tags",
        header: "Tags",
        width: 180,
        render: (r: Job) =>
          r.tags && r.tags.length ? (
            <div className="flex flex-wrap gap-1">
              {r.tags.map((t) => (
                <span key={t} className="badge">
                  {t}
                </span>
              ))}
            </div>
          ) : (
            "—"
          ),
      },
      {
        key: "actions",
        header: "",
        width: 120,
        render: (r: Job) => (
          <div className="flex justify-end gap-2">
            <Link
              to={`/jobs/${encodeURIComponent(r.id)}`}
              className={`btn ${canEdit(role) ? "" : "pointer-events-none opacity-50"}`}
              aria-disabled={!canEdit(role)}
              onClick={() =>
                logAudit({ action: "open_job", jobId: r.id, role }, role)
              }
            >
              Edit
            </Link>
          </div>
        ),
      },
    ],
    [role]
  );

  /** Presets */
  const savePreset = () => {
    const name = window.prompt("Save current filters as…");
    if (!name) return;
    const preset: Preset = {
      id: crypto.randomUUID(),
      name,
      filters: {
        q: filters.q,
        status: filters.status,
        hasGeofence: filters.hasGeofence,
        start: filters.start,
        end: filters.end,
      },
    };
    const next = [...presets, preset];
    setPresets(next);
    set(SK.presets, next);
    setActivePresetId(preset.id);

    push({ title: "Saved view created", body: `"${name}" saved.` });
    logAudit({ action: "jobs:preset:save", name, filters: preset.filters }, role);
  };

  const applyPreset = (id: string) => {
    setActivePresetId(id);
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setFilters((f) => ({ ...f, ...p.filters }));
    logAudit({ action: "jobs:preset:apply", presetId: id }, role);
  };

  const deletePreset = (id: string) => {
    const p = presets.find((x) => x.id === id);
    const next = presets.filter((x) => x.id !== id);
    setPresets(next);
    set(SK.presets, next);
    if (activePresetId === id) setActivePresetId("");
    push({ title: "Saved view deleted", body: p ? `"${p.name}" removed.` : "Removed." });
    logAudit({ action: "jobs:preset:delete", presetId: id }, role);
  };

  const clearFilters = () => {
    setFilters((f) => ({ ...DEFAULT_FILTERS, density: f.density }));
    setActivePresetId("");
    logAudit({ action: "jobs:filters:clear" }, role);
  };

  const onSeed = () => {
    const existing = get<Job[]>(SK.data, []);
    if (existing.length) {
      push({ title: "Data already present", body: "Jobs data exists; not seeding." });
      return;
    }
    set(SK.data, SAMPLE);
    setJobs(SAMPLE);
    push({ title: "Sample data seeded", body: "Added 3 sample jobs." });
    logAudit({ action: "jobs:seed", count: SAMPLE.length }, role);
  };

  return (
    <AppShell title="Jobs">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="text-xl font-semibold">Jobs</div>
          <div className="opacity-60 text-sm">
            ({rows.length.toLocaleString()} of {jobs.length.toLocaleString()})
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            className="btn btn-secondary"
            onClick={() =>
              setFilters((f) => ({
                ...f,
                density: f.density === "cozy" ? "compact" : "cozy",
              }))
            }
            title="Toggle density"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            {filters.density === "cozy" ? "Cozy" : "Compact"}
          </button>

          {canCreate(role) ? (
            <Link
              to="/jobs/new"
              className="btn"
              onClick={() => logAudit({ action: "jobs:create:click", role }, role)}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Job
            </Link>
          ) : null}
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-header flex items-center justify-between">
          <div className="font-medium">Filters</div>
          <div className="flex items-center gap-2">
            <select
              className="select"
              value={activePresetId}
              onChange={(e) => applyPreset(e.target.value)}
              aria-label="Saved views"
            >
              <option value="">Saved views…</option>
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <button className="btn btn-secondary" onClick={savePreset} title="Save current filters">
              <Save className="w-4 h-4 mr-2" />
              Save View
            </button>
            {activePresetId ? (
              <button
                className="btn btn-danger"
                onClick={() => deletePreset(activePresetId)}
                title="Delete selected view"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete View
              </button>
            ) : null}
          </div>
        </div>

        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <input
              className="input md:col-span-2"
              placeholder="Search jobs, sites, customers, tags…"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            />
            <select
              className="select"
              value={filters.status}
              onChange={(e) =>
                setFilters((f) => ({ ...f, status: e.target.value as Filters["status"] }))
              }
            >
              <option value="any">Any status</option>
              <option value="Draft">Draft</option>
              <option value="Active">Active</option>
              <option value="Paused">Paused</option>
              <option value="Completed">Completed</option>
              <option value="Archived">Archived</option>
            </select>
            <select
              className="select"
              value={filters.hasGeofence}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  hasGeofence: e.target.value as Filters["hasGeofence"],
                }))
              }
            >
              <option value="any">Geofence: Any</option>
              <option value="yes">Geofence: Yes</option>
              <option value="no">Geofence: No</option>
            </select>
            <div className="flex gap-2">
              <input
                type="date"
                className="input"
                value={filters.start}
                onChange={(e) => setFilters((f) => ({ ...f, start: e.target.value }))}
                aria-label="Start date"
              />
              <input
                type="date"
                className="input"
                value={filters.end}
                onChange={(e) => setFilters((f) => ({ ...f, end: e.target.value }))}
                aria-label="End date"
              />
            </div>
          </div>

          <div className="mt-2 flex items-center justify-between">
            <div className="text-sm opacity-70">
              Showing <span className="font-medium">{rows.length.toLocaleString()}</span> of{" "}
              <span className="font-medium">{jobs.length.toLocaleString()}</span> jobs
            </div>
            <div className="flex items-center gap-2">
              <button className="btn btn-secondary" onClick={clearFilters}>
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div
        className={`table-shell ${
          filters.density === "compact" ? "density-compact" : ""
        } table-sticky table-zebra`}
      >
        {loading ? (
          <Skeleton className="h-48" />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No jobs match your filters"
            action={
              canCreate(role) ? (
                <div className="flex items-center gap-2">
                  <Link to="/jobs/new" className="btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Job
                  </Link>
                  <button className="btn btn-secondary" onClick={onSeed}>
                    Seed sample data
                  </button>
                </div>
              ) : (
                <button className="btn btn-secondary" onClick={onSeed}>
                  Seed sample data
                </button>
              )
            }
          >
            <p className="text-sm text-slate-600">
              Try adjusting the filters. If you haven&apos;t added jobs yet, start a new one.
            </p>
          </EmptyState>
        ) : (
          <>
            <DataTable columns={cols} rows={rows} filename="jobs" storageKey={SK.table} />
            <div className="mt-3 px-3 py-2 border rounded">
              <div className="flex flex-wrap items-center gap-6 justify-end tabular-nums">
                <div>
                  <span className="opacity-70 mr-1">Loads:</span>
                  <span className="font-medium">{fmtInt(totals.loads)}</span>
                </div>
                <div>
                  <span className="opacity-70 mr-1">Tons:</span>
                  <span className="font-medium">{fmtInt(totals.tons)}</span>
                </div>
                <div>
                  <span className="opacity-70 mr-1">Est. Tons:</span>
                  <span className="font-medium">{fmtInt(totals.est)}</span>
                </div>
                <div>
                  <span className="opacity-70 mr-1">Profit:</span>
                  <span className="font-medium">{fmtCurrency(totals.profit)}</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
